import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { InspirationSource } from '@/types/webhook';
import { logSupabaseError, logError } from '@/utils/sentry';

interface UserProfileData {
  instagram_username: string;
  category: string; // This will store the industry selection
}

interface OnboardingError {
  message: string;
  code?: string;
}

export const useOnboarding = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  /**
   * Save user profile data (industry + Instagram username)
   */
  const saveUserProfile = async (
    userId: string, 
    data: UserProfileData
  ): Promise<{ success: boolean; error?: OnboardingError }> => {
    if (!userId) {
      const error = { message: 'User ID is required' };
      return { success: false, error };
    }

    setIsLoading(true);
    
    try {
      // First, check if profile exists
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      // Build update object with only non-empty fields
      const updateData: {
        user_id: string;
        updated_at: string;
        instagram_username?: string;
        category?: string;
      } = {
        user_id: userId,
        updated_at: new Date().toISOString()
      };

      if (data.instagram_username) {
        // Validate that it's not an email address
        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.instagram_username);
        if (isEmail) {
          console.error('[useOnboarding] Attempted to save email as Instagram username:', data.instagram_username);
          const error = { message: 'Instagram username cannot be an email address' };
          return { success: false, error };
        }

        // Validate Instagram username format
        const isValidFormat = /^[a-zA-Z0-9._]+$/.test(data.instagram_username);
        if (!isValidFormat) {
          console.error('[useOnboarding] Invalid Instagram username format:', data.instagram_username);
          const error = { message: 'Instagram username contains invalid characters' };
          return { success: false, error };
        }

        updateData.instagram_username = data.instagram_username;
      }
      
      if (data.category) {
        updateData.category = data.category;
      }

      // If profile exists, preserve existing values for fields not being updated
      if (existingProfile) {
        if (!data.instagram_username && existingProfile.instagram_username) {
          updateData.instagram_username = existingProfile.instagram_username;
        }
        if (!data.category && existingProfile.category) {
          updateData.category = existingProfile.category;
        }
      }

      // Refuse to create a brand-new row without a real Instagram username.
      // The column is NOT NULL, so the historical fallback was a sentinel
      // ("unknown") — but that sentinel could never be cleared from the UI
      // (Settings only ADDs when none exists), permanently locking the user
      // out of fixing their handle. The caller must go through
      // InstagramSetup first.
      if (!existingProfile && !updateData.instagram_username) {
        return {
          success: false,
          error: { message: 'Instagram username required to create profile' },
        };
      }

      const upsertData = {
        ...updateData,
        // Existing row guarantees a NOT NULL value via the preservation above
        // or a fresh value from `data.instagram_username`.
        instagram_username:
          updateData.instagram_username ||
          existingProfile?.instagram_username ||
          '',
      };
      
      // Use upsert to handle both insert and update cases
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert(upsertData, {
          onConflict: 'user_id'
        });

      if (profileError) {
        logSupabaseError('Onboarding: save profile', profileError);
        const error = { 
          message: 'Failed to save profile data',
          code: profileError.code 
        };
        return { success: false, error };
      }

      
      return { success: true };

    } catch (error) {
      logError('Onboarding: unexpected profile error', error, { step: 'profile' });
      const errorObj = { 
        message: 'An unexpected error occurred while saving profile'
      };
      return { success: false, error: errorObj };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Save inspiration sources to the database
   */
  const saveInspirationSources = async (
    userId: string, 
    sources: InspirationSource[]
  ): Promise<{ success: boolean; error?: OnboardingError; savedCount?: number }> => {
    if (!userId) {
      const error = { message: 'User ID is required' };
      return { success: false, error };
    }

    if (!sources.length) {
      const error = { message: 'At least one inspiration source is required' };
      return { success: false, error };
    }

    setIsLoading(true);
    
    try {
      
      
      // Insert sources sequentially to handle tier-based DB triggers gracefully
      let savedCount = 0;
      for (const source of sources) {
        const { error: insertError } = await supabase
          .from('inspiration_sources')
          .insert({
            user_id: userId,
            username: source.username,
            platform: source.platform,
            scraping_status: 'pending',
            created_at: new Date().toISOString()
          });

        if (insertError) {
          console.warn('Source insert stopped:', insertError.message);
          break; // Stop at first failure (limit reached)
        }
        savedCount++;
      }

      if (savedCount === 0) {
        const error = { message: 'Failed to save any inspiration sources' };
        return { success: false, error };
      }

      return { success: true, savedCount };

    } catch (error) {
      logError('Onboarding: unexpected sources error', error, { step: 'sources' });
      const errorObj = { 
        message: 'An unexpected error occurred while saving inspiration sources'
      };
      return { success: false, error: errorObj };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Complete onboarding by saving all data
   */
  const completeOnboarding = async (
    userId: string,
    profileData: UserProfileData,
    sources: InspirationSource[]
  ): Promise<{ success: boolean; error?: OnboardingError }> => {
    setIsLoading(true);
    
    try {
      // Save profile first
      const profileResult = await saveUserProfile(userId, profileData);
      if (!profileResult.success) {
        return profileResult;
      }

      // Save inspiration sources
      const sourcesResult = await saveInspirationSources(userId, sources);
      if (!sourcesResult.success) {
        return sourcesResult;
      }

      return { success: true };

    } catch (error) {
      logError('Onboarding: completion error', error, { step: 'complete' });
      const errorObj = { 
        message: 'An unexpected error occurred during onboarding completion'
      };
      return { success: false, error: errorObj };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get existing user profile
   */
  const getUserProfile = async (userId: string) => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        logSupabaseError('Onboarding: get profile', error);
        return null;
      }

      return data;
    } catch (error) {
      logError('Onboarding: unexpected get profile error', error, { step: 'getProfile' });
      return null;
    }
  };

  return {
    saveUserProfile,
    saveInspirationSources,
    completeOnboarding,
    getUserProfile,
    isLoading
  };
};